import React, { useEffect, useState } from "react";
import "../../../Styles/components/form-controls.scss";

function normalizeAmountTypingValue(value, trimLeadingZeroes = false) {
  const source = String(value ?? "");
  const cleaned = source.replace(/[^\d.]/g, "");

  if (!cleaned) return "";

  const [integerPart, ...decimalParts] = cleaned.split(".");
  const normalizedInteger = trimLeadingZeroes
    ? integerPart.replace(/^0+(?=\d)/, "")
    : integerPart;

  if (decimalParts.length === 0) return normalizedInteger;

  return `${normalizedInteger}.${decimalParts.join("")}`;
}

function emitChange(onChange, nextValue, nativeEvent) {
  if (!onChange) return;

  onChange({
    ...nativeEvent,
    target: {
      ...(nativeEvent?.target || {}),
      value: nextValue,
    },
    currentTarget: {
      ...(nativeEvent?.currentTarget || {}),
      value: nextValue,
    },
  });
}

export default function AmountInput({
  className = "",
  disabled = false,
  error = "",
  label = "",
  onChange,
  onBlur,
  onFocus,
  prefix = "",
  formatDraftValue,
  clearZeroOnFocus = false,
  trimLeadingZeroes = false,
  value = "",
  ...props
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [draftValue, setDraftValue] = useState(String(value ?? ""));

  useEffect(() => {
    if (!isFocused) {
      setDraftValue(String(value ?? ""));
    }
  }, [value, isFocused]);

  const handleFocus = (event) => {
    setIsFocused(true);
    const focusValue = String(value ?? "");
    const shouldClearZero =
      clearZeroOnFocus &&
      (focusValue === "0" ||
        focusValue === "0.0" ||
        focusValue === "0.00" ||
        focusValue === "0.000");
    setDraftValue(shouldClearZero ? "" : focusValue);
    onFocus?.(event);
  };

  const handleChange = (event) => {
    const nextRaw = normalizeAmountTypingValue(
      event.target.value,
      trimLeadingZeroes,
    );
    const nextDraftValue = formatDraftValue
      ? String(formatDraftValue(nextRaw) ?? "")
      : nextRaw;
    setDraftValue(nextDraftValue);
    emitChange(onChange, nextRaw, event);
  };

  const handleBlur = (event) => {
    setIsFocused(false);
    const finalValue = normalizeAmountTypingValue(
      draftValue,
      trimLeadingZeroes,
    );
    setDraftValue(finalValue);
    emitChange(onChange, finalValue, event);
    onBlur?.(event);
  };

  return (
    <div className={["form-field", className].filter(Boolean).join(" ")}>
      {label ? <label className="form-field__label">{label}</label> : null}
      <div className={`amount-field ${error ? "has-error" : ""}`}>
        {prefix ? <span className="amount-field__prefix">{prefix}</span> : null}
        <input
          type="text"
          inputMode="decimal"
          value={isFocused ? draftValue : String(value ?? "")}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          className="amount-field__control"
          {...props}
        />
      </div>
      {error ? <div className="form-field__error">{error}</div> : null}
    </div>
  );
}
